import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { IconAlert, IconEye, IconEyeOff, IconSpinner } from '../components/Icons';

export default function Login() {
  const navigate = useNavigate();
  const { login, isLoading, error } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await login(username, password);
    if (success) {
      navigate('/');
    }
  };

  const inputClass =
    'h-11 w-full px-3.5 text-[15px] bg-white text-ink border border-line-strong rounded-lg ' +
    'placeholder:text-[#9AA3B0] focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

  return (
    <div className="min-h-screen flex flex-wrap bg-ground text-ink">
      {/* Brand panel */}
      <section className="flex-[1_1_480px] min-w-0 min-h-[320px] bg-brand text-white px-8 py-10 md:px-16 md:py-14 flex flex-col justify-between gap-12">
        <div className="flex items-center gap-3.5">
          <div className="w-[72px] h-[72px] rounded-2xl bg-white flex items-center justify-center shrink-0">
            <img src="/logo.png" alt="喵喵團報" width={68} height={68} className="w-[68px] h-[68px] object-contain" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-lg font-bold tracking-wide">喵喵團報</span>
            <span className="text-[13px] text-brand-muted">管理後台</span>
          </div>
        </div>

        <div className="flex flex-col gap-5 max-w-[480px]">
          <h1 className="text-3xl md:text-[44px] md:leading-[1.25] font-bold">
            活動、報名、收款，<br />在同一個後台處理。
          </h1>
          <p className="text-base leading-7 text-brand-muted">
            建立團體報名表單、審核付款、匯出名單。報名者透過 LINE 填寫，資料即時進到這裡。
          </p>
        </div>

        <div className="flex items-center gap-4 text-[13px] text-brand-faint">
          <span>© 2026 喵喵團報</span>
        </div>
      </section>

      {/* Form panel */}
      <section className="flex-[1_1_480px] min-w-0 px-6 py-12 md:px-8 md:py-14 flex items-center justify-center">
        <form onSubmit={handleSubmit} className="w-full max-w-[400px] flex flex-col gap-7">
          <div className="flex flex-col gap-2">
            <h2 className="text-[26px] font-bold leading-tight">登入管理後台</h2>
            <p className="text-sm text-muted">請使用管理員帳號登入。</p>
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 px-3.5 py-3 bg-bad-soft border border-[#F3C2C2] rounded-lg text-[#8A1C1C] text-sm leading-relaxed"
            >
              <IconAlert size={18} className="shrink-0 mt-px" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col gap-[18px]">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="username" className="text-sm font-medium">帳號</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={inputClass}
                placeholder="請輸入帳號"
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <label htmlFor="password" className="text-sm font-medium">密碼</label>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  className="inline-flex items-center gap-1.5 text-[13px] text-primary hover:text-primary-dark py-1"
                >
                  {showPassword ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                  {showPassword ? '隱藏密碼' : '顯示密碼'}
                </button>
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="請輸入密碼"
                required
                autoComplete="current-password"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <button
              type="submit"
              disabled={isLoading}
              className="h-[46px] w-full bg-primary text-white text-[15px] font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <IconSpinner size={18} />
                  登入中
                </>
              ) : (
                '登入'
              )}
            </button>
            <p className="text-[13px] text-muted text-center leading-relaxed">
              忘記密碼？請聯絡系統管理員重設。
            </p>
          </div>
        </form>
      </section>
    </div>
  );
}
